import { z } from 'zod';

import { QuestionVisibilityPolicySchema } from '../../visibility/resolve-visibility/resolve-visibility';

/**
 * Applying visibility defaults to **every question of one section** —
 * `03-visibility-policy.md` §Admin's "bulk defaults per section".
 *
 * A **patch, not a policy**, and that is the whole design. `Create` and `Update`
 * take the complete four-column `QuestionVisibilityPolicySchema` because one
 * question's exposure is decided as a whole. Applied across a section, the same
 * shape would overwrite three facets nobody meant to touch every time an
 * operator wanted to change one — "hide everything in this section publicly" is
 * a sentence about the public context alone, and there is no undo. The four
 * facets are independently meaningful, so each is optional and **at least one**
 * must be named; a client that does mean the whole policy still sends all four.
 *
 * This does not weaken the discipline that a question is never *born* with an
 * exposure nobody decided — which lives in two places, neither of them here:
 * `CreateProfileQuestionSchema.visibility` is required, and the two **mode**
 * columns are `notNull` with no database default, so a row cannot be inserted
 * without one. (The two `defaultVisible*` booleans *do* carry column defaults;
 * they only matter under `user_controlled`.) A partial `UPDATE` of rows that
 * already hold values cannot produce a NULL in any of the four.
 *
 * The section is a path parameter, matching `PUT admin/questions/reorder/:sectionId`.
 * The patch is wrapped in `visibility` rather than being the body itself, so it
 * reads the same as the single-question write and leaves room for a narrowing
 * field later without changing the shape clients already send.
 */
export const SectionVisibilityDefaultsSchema = z
  .object({
    visibility: QuestionVisibilityPolicySchema.partial(),
  })
  .strict()
  .refine((body) => Object.keys(body.visibility).length > 0, {
    message: 'Name at least one part of the policy to apply.',
    path: ['visibility'],
  });

export type SectionVisibilityDefaultsRequest = z.infer<typeof SectionVisibilityDefaultsSchema>;
